import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { getVioletDreams, saveVioletDreams } from "./localStorageUtils";

const positiveWords = ["good", "great", "amazing", "happy", "love", "wonderful", "awesome"];
const negativeWords = ["bad", "terrible", "sad", "hate", "awful", "angry", "tired"];

function naiveSentimentAnalysis(text) {
  const lower = text.toLowerCase();
  let score = 0;
  positiveWords.forEach((w) => {
    if (lower.includes(w)) score += 1;
  });
  negativeWords.forEach((w) => {
    if (lower.includes(w)) score -= 1;
  });
  return score;
}

const successPool = [
  "Mmm~ This dream pleases me. You danced with shadows well.",
  "You wrote sweet words, but I see the depths behind them... I'm impressed.",
  "Chaos resonates, but you remain afloat. Good~",
  "Your day flirts with cosmic potential. Keep going."
];
const failurePool = [
  "Oh dear, you drowned in negativity? Tsk, so mortal.",
  "I taste bitterness in your day. Next time, more courage, yes?",
  "Your mind wavered. I'm disappointed. Fix it, dear~",
  "Did the curses consume you? So soon?"
];
const cosmicPool = [
  "Reality twisted in your day… fascinating. Are you sure you woke up?",
  "I sense an echo from beyond. This day was no normal day, was it?",
  "Your day defies mortal logic. Mmm, I like it~"
];

const VioletDreamJournal = () => {
  const [entries, setEntries] = useState([]);
  const [currentText, setCurrentText] = useState("");
  const [reactionPopup, setReactionPopup] = useState(null);
  const [weeklyOutcome, setWeeklyOutcome] = useState("neutral");
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getVioletDreams();
        console.log("Fetched violet dreams data:", data);
        if (data && data.entries) {
          setEntries(data.entries);
        }
      } catch (err) {
        console.error("Failed to load Violet Dreams:", err);
        setError("Failed to load your dreams. Check console for details.");
      }
    })();
  }, []);

  useEffect(() => {
    try {
      const outcome = localStorage.getItem("weeklyOutcome") || "neutral";
      setWeeklyOutcome(outcome);
    } catch (err) {
      console.error("Failed to get weeklyOutcome from localStorage:", err);
      setError("Error retrieving weekly outcome.");
    }
  }, []);

  const closePopup = () => setReactionPopup(null);

  const handleAddEntry = async () => {
    if (!currentText.trim()) return;

    try {
      const sentiment = naiveSentimentAnalysis(currentText);
      let finalVerdict = "Neutral Day";
      if (weeklyOutcome === "cursed" && sentiment < -1) {
        finalVerdict = "Cosmic Disturbance";
      } else if (weeklyOutcome === "blessed" && sentiment >= 2) {
        finalVerdict = "Radiant Day";
      } else {
        finalVerdict = sentiment < 0 ? "Bad Day" : "Good Day";
      }

      let reactionLines = [];
      if (finalVerdict === "Cosmic Disturbance") {
        reactionLines = cosmicPool;
      } else if (finalVerdict.includes("Good") || finalVerdict === "Radiant Day") {
        reactionLines = successPool;
      } else if (finalVerdict.includes("Bad")) {
        reactionLines = failurePool;
      } else {
        reactionLines = cosmicPool;
      }
      const line = reactionLines[Math.floor(Math.random() * reactionLines.length)];
      setReactionPopup({ verdict: finalVerdict, message: line });

      const newEntry = {
        id: Date.now(),
        date: new Date().toLocaleDateString(),
        text: currentText,
        sentiment,
        weeklyOutcome,
        finalVerdict,
      };

      const updated = [...entries, newEntry];
      setEntries(updated);
      setCurrentText("");
      await saveVioletDreams(updated);
    } catch (err) {
      console.error("Error adding entry:", err);
      setError("Error saving your dream entry.");
    }
  };

  const chartData = entries.map((e, idx) => ({
    index: idx + 1,
    sentiment: e.sentiment,
  }));

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-r from-purple-900 via-black to-gray-900 p-4 md:p-8 text-white">
        <h1 className="text-3xl md:text-4xl font-extrabold mb-6 text-center">Violet's Dream Journal</h1>
        <p className="text-center text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-purple-900 via-black to-gray-900 p-4 md:p-8 text-white">
      <h1 className="text-3xl md:text-4xl font-extrabold mb-6 text-center">Violet's Dream Journal</h1>
      <p className="text-lg md:text-xl text-center italic mb-10">
        “Write your day, let me see the hidden truths…”
      </p>
      <div className="mb-4 text-center">
        <span className="text-lg">Weekly Outcome: </span>
        <span className="font-bold text-pink-400">{weeklyOutcome.toUpperCase()}</span>
      </div>
      <section className="mb-8 bg-purple-800 p-4 md:p-6 rounded-md shadow-md max-w-2xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-3">Write Your Day or Dream</h2>
        <textarea
          className="w-full h-32 p-2 rounded text-black"
          placeholder="Describe your day or dream, illusions, nightmares, successes..."
          value={currentText}
          onChange={(e) => setCurrentText(e.target.value)}
        />
        <button
          onClick={handleAddEntry}
          className="mt-4 px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-lg font-bold w-full"
        >
          Add Entry
        </button>
      </section>
      <section className="mb-8">
        <div className="bg-purple-900 p-4 rounded shadow-md">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Sentiment Over Time</h2>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#666" />
                <XAxis dataKey="index" stroke="#fff" />
                <YAxis stroke="#fff" />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sentiment" stroke="#ff00ff" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center">No data to display.</p>
          )}
        </div>
      </section>
      <section className="mb-8">
        <h2 className="text-2xl md:text-3xl font-bold mb-3">Past Entries</h2>
        {entries.length ? (
          <div className="space-y-4">
            {entries.map((entry) => (
              <div key={entry.id} className="bg-purple-700 p-4 rounded shadow-md">
                <p className="text-sm opacity-80 mb-1">{entry.date}</p>
                <p className="text-base mb-2 whitespace-pre-wrap">{entry.text}</p>
                <p className="text-sm">
                  Sentiment Score: <span className="font-bold">{entry.sentiment}</span> — Final Verdict:{" "}
                  <span className="font-bold">{entry.finalVerdict}</span>
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center">No entries yet. Add one!</p>
        )}
      </section>
      {reactionPopup &&
        createPortal(
          <div
            className="fixed top-10 left-10 bg-black bg-opacity-80 text-white p-4 rounded shadow-lg z-[9999]"
            style={{ maxWidth: "300px" }}
          >
            <h3 className="font-bold text-xl mb-2 text-pink-400">{reactionPopup.verdict}</h3>
            <p>{reactionPopup.message}</p>
            <button onClick={() => setReactionPopup(null)} className="mt-3 bg-gray-300 text-black px-3 py-1 rounded hover:bg-gray-400">
              Close
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default VioletDreamJournal;
